import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { EdgeDetail } from "../../../services/graphService";

interface EdgeHeaderProps {
  edgeDetail: EdgeDetail;
  onClose: () => void;
  mode?: "overlay" | "standalone";
}

const EdgeHeader: React.FC<EdgeHeaderProps> = ({
  edgeDetail,
  onClose,
  mode = "overlay",
}) => {
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    window.history.back();
  }, []);

  const handleForward = useCallback(() => {
    window.history.forward();
  }, []);

  const handleOpenInFullView = useCallback(() => {
    if (edgeDetail) {
      navigate(`/relation/${edgeDetail.id}`);
    }
  }, [edgeDetail, navigate]);

  const handleOpenInNewTab = useCallback(() => {
    if (edgeDetail) {
      window.open(`/relation/${edgeDetail.id}`, "_blank");
    }
  }, [edgeDetail]);

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
        }}
      >
        {/* Left: Title */}
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <LinkIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6">Relationship Details</Typography>
        </Box>

        {/* Center: Action buttons */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        ></Box>

        {/* Right: Navigation buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-end",
            gap: 0.5,
          }}
        >
          {/* Open in New Tab button - only visible in overlay mode */}
          {mode === "overlay" && edgeDetail && (
            <IconButton
              onClick={handleOpenInNewTab}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover" },
              }}
              title="Open in New Tab"
            >
              <OpenInNewIcon />
            </IconButton>
          )}
          {/* Open in Full View button - only visible in overlay mode */}
          {mode === "overlay" && edgeDetail && (
            <IconButton
              onClick={handleOpenInFullView}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover" },
              }}
              title="Open in Full View"
            >
              <FullscreenIcon />
            </IconButton>
          )}
          <IconButton
            onClick={handleBack}
            size="small"
            title="Go Back"
            sx={{
              color: "text.secondary",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={handleForward}
            size="small"
            title="Go Forward"
            sx={{
              color: "text.secondary",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
          {/* Close button - only visible in overlay mode */}
          {mode === "overlay" && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Relationship Overview - Centered in standalone mode */}
      <Box
        sx={{
          display: "flex",
          justifyContent: mode === "standalone" ? "center" : "flex-start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: mode === "standalone" ? "1200px" : "100%",
            pt: 3,
            pb: 0,
            pl: 5,
            pr: 4,
            flexShrink: 0,
          }}
        >
          <Typography variant="h4" gutterBottom>
            {edgeDetail.source_entity.name} → {edgeDetail.relation_type} →{" "}
            {edgeDetail.target_entity.name}
          </Typography>

          <Box sx={{ mb: 3 }}>
            {/* Relationship Type Chip */}
            <Chip
              label={edgeDetail.relation_type}
              variant="filled"
              sx={{ mr: 1, mb: 0.5 }}
            />

            {/* Additional relationship metadata chips if available */}
            {edgeDetail.relation_status && (
              <Chip
                label={edgeDetail.relation_status}
                variant="outlined"
                color={
                  edgeDetail.relation_status.toLowerCase() === "active"
                    ? "success"
                    : edgeDetail.relation_status
                          .toLowerCase()
                          .includes("inactive") ||
                        edgeDetail.relation_status
                          .toLowerCase()
                          .includes("terminated")
                      ? "error"
                      : "warning"
                }
                sx={{ mr: 1, mb: 0.5 }}
              />
            )}
          </Box>

          <Typography variant="body1" color="text.secondary" paragraph>
            {edgeDetail.relation_summary || "No relationship summary available"}
          </Typography>

          {/* Quality Metrics - positioned right below summary */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AssessmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">
                Quality Metrics:
              </Typography>
            </Box> */}

            {edgeDetail.confidence_percentage && (
              <Chip
                label={`${edgeDetail.confidence_percentage} Confidence`}
                size="small"
                color={
                  parseFloat(edgeDetail.confidence_percentage) >= 80
                    ? "success"
                    : parseFloat(edgeDetail.confidence_percentage) >= 60
                      ? "warning"
                      : "error"
                }
                variant="outlined"
              />
            )}

            {edgeDetail.count && (
              <Chip
                label={`${edgeDetail.count} Sources`}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default EdgeHeader;
